import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * LocationSuggestionsV2 - Dropdown component for displaying city search suggestions
 * Supports keyboard navigation and accessibility features
 */
const LocationSuggestionsV2 = ({
  suggestions = [],
  onSelect,
  loading = false,
  visible = false,
  selectedIndex = -1,
  onKeyboardNavigation,
  className = '',
  maxHeight = '200px'
}) => {
  const listRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(selectedIndex);

  // Update focused index when selectedIndex prop changes
  useEffect(() => {
    setFocusedIndex(selectedIndex);
  }, [selectedIndex]);

  // Scroll focused item into view
  useEffect(() => {
    if (listRef.current && focusedIndex >= 0) {
      const focusedElement = listRef.current.children[focusedIndex];
      if (focusedElement) {
        focusedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [focusedIndex]);

  /**
   * Handle mouse enter on suggestion item
   */
  const handleMouseEnter = useCallback((index) => {
    setFocusedIndex(index);
    if (onKeyboardNavigation) {
      onKeyboardNavigation(index);
    }
  }, [onKeyboardNavigation]);

  /**
   * Handle click on suggestion item
   */
  const handleClick = useCallback((suggestion, index) => {
    if (onSelect) {
      onSelect(suggestion, index);
    }
  }, [onSelect]);

  /**
   * Format city display text
   */
  const formatCityDisplay = useCallback((suggestion) => {
    const parts = [];
    
    if (suggestion.city) {
      parts.push(suggestion.city);
    }
    
    if (suggestion.state) {
      parts.push(suggestion.state);
    }
    
    if (suggestion.country) {
      parts.push(suggestion.country);
    }
    
    return parts.join(', ');
  }, []);

  // Don't render if not visible
  if (!visible) {
    return null;
  }

  return (
    <div
      className={`location-suggestions-v2 ${className}`}
      role="listbox"
      aria-label="City suggestions"
      style={{ maxHeight }}
    >
      {loading && (
        <div className="location-suggestion-loading">
          <div className="spinner" />
          Searching cities...
        </div>
      )}

      {!loading && suggestions.length === 0 && (
        <div className="location-suggestion-empty">
          No cities found
        </div>
      )}

      {!loading && suggestions.length > 0 && (
        <ul
          ref={listRef}
          className="location-suggestions-list"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id || index}
              role="option"
              aria-selected={index === focusedIndex}
              className={`location-suggestion-item ${
                index === focusedIndex ? 'focused' : ''
              }`}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseDown={(e) => {
                // Prevent blur event from firing before click
                e.preventDefault();
              }}
              onClick={() => handleClick(suggestion, index)}
            >
              <div className="location-suggestion-content">
                <div className="location-suggestion-primary">
                  {suggestion.displayName || suggestion.city}
                </div>
                <div className="location-suggestion-secondary">
                  {formatCityDisplay(suggestion)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

LocationSuggestionsV2.propTypes = {
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      country: PropTypes.string,
      formattedAddress: PropTypes.string
    })
  ),
  onSelect: PropTypes.func,
  loading: PropTypes.bool,
  visible: PropTypes.bool,
  selectedIndex: PropTypes.number,
  onKeyboardNavigation: PropTypes.func,
  className: PropTypes.string,
  maxHeight: PropTypes.string
};

export default LocationSuggestionsV2;